import React from "react";
import { createContext, useState } from "react";
import { CardViewOption } from "../../components/CardItem/ToggleCardView";

export interface IPenelContextProvider {
	expandedPanel: PanelId;
	bigCardDocument: number;
	cardView: {
		documentId: number;
		view: CardViewOption;
	} | null;
	setBigCardDocument: React.Dispatch<React.SetStateAction<number>>;
	toggleCardView: (documentId: number, view: CardViewOption) => void;
	closeCardView: () => void;
	setExpandedPanel: React.Dispatch<React.SetStateAction<PanelId>>;
}

export enum PanelId {
	Navigation = "NavigationBurgerPanel",
	Filter = "FilterBarPanel",
	Date = "DatePanel",
	SearchTerm = "SearchTerm",
}

interface PanelContextProps {
	children?: React.ReactNode;
}

const PanelContext = createContext({} as IPenelContextProvider);

const PanelContextProvider: React.FunctionComponent<PanelContextProps> = ({
	children,
}) => {
	const [expandedPanel, setExpandedPanel] = useState<PanelId>(null);
	const [bigCardDocument, setBigCardDocument] = useState<number>(null);
	const [cardView, setCardView] = useState<{
		documentId: number | null;
		view: CardViewOption;
	} | null>(null);

	const toggleCardView = (documentId: number, view: CardViewOption) => {
		if (view === CardViewOption.SUMMARY || view === null) {
			closeCardView();
		} else {
			setCardView({ documentId, view });
		}
	};

	const closeCardView = () => {
		setCardView(null);
	};

	const clientAccessContextValue = {
		expandedPanel,
		bigCardDocument,
		cardView,
		setBigCardDocument,
		setExpandedPanel,
		toggleCardView,
		closeCardView,
	};

	return (
		<PanelContext.Provider value={clientAccessContextValue}>
			{children}
		</PanelContext.Provider>
	);
};

const usePanelContext = () => {
	const context = React.useContext(PanelContext);
	if (context === undefined) {
		throw new Error(
			"usePanelContext must be used within a PanelContextProvider"
		);
	}
	return context;
};

export { PanelContextProvider, usePanelContext };
